/**
 * Candid authorization POST and post-success side effects for the
 * Auth Letter Upload modal.
 *
 * @module useCandidAuthorizationUpload
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { CandidService } from "../../../../api/services/candid/candidService";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { AuthLetterUploadFormState } from "../../../../data-model/candidAuthorizationModel";
import {
  CandidAuthorizationsResponse,
  CandidWrittenAuthorization,
} from "../../../../data-model/candidAuthorizationsResponse";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { buildCandidAuthorizationsRequest } from "../buildCandidAuthorizationsRequest";

/**
 * Unique CPT/HCPCS codes from written authorization pairs, first-seen order.
 *
 * @param writtenAuthorizations - Combinations actually written to Candid
 * @returns Distinct J-codes, or an empty list when none were written
 */
const uniqueWrittenJCodes = (
  writtenAuthorizations: CandidWrittenAuthorization[] | undefined,
): string[] => {
  const codes: string[] = [];
  for (const entry of writtenAuthorizations ?? []) {
    const code = entry.cpt_code?.trim();
    if (code && !codes.includes(code)) {
      codes.push(code);
    }
  }
  return codes;
};

/**
 * Success toast after an authorizations POST.
 *
 * Empty `written_authorizations` means every pair already existed on the
 * patient. Otherwise the toast lists the J-codes that were created.
 *
 * @param writtenAuthorizations - Combinations actually written to Candid
 * @returns Toast copy for the write-back outcome
 */
const authorizationWritebackToastMessage = (
  writtenAuthorizations: CandidWrittenAuthorization[] | undefined,
): string => {
  const jCodes = uniqueWrittenJCodes(writtenAuthorizations);
  if (jCodes.length === 0) {
    return ToastMessages.CANDID_AUTHORIZATION_EXISTS;
  }
  return `${ToastMessages.CANDID_AUTHORIZATION_CREATED} ${jCodes.join(", ")}`;
};

/**
 * Builds the authorizations POST and the success handler (refetch, close,
 * toast). Failures are left to {@link useAuthSaveFlow}.
 *
 * @returns `onUpload` and `onUploadSuccess` callbacks
 */
export const useCandidAuthorizationUpload = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { generalOrderDetails, fetchAll } = useDataAccumulatorForSingleOrder();

  /**
   * Builds the Candid authorizations request from the authorization tabs and
   * posts it. Called only on the final tab's Save click.
   *
   * @param authData - Authorization tabs to upload
   * @returns Written-authorizations envelope from coverage-service
   */
  const onUpload = useCallback(
    async (
      authData: AuthLetterUploadFormState[],
    ): Promise<CandidAuthorizationsResponse> => {
      const payload = buildCandidAuthorizationsRequest(authData, {
        orderId: generalOrderDetails?.order?.order_id ?? "",
        orgId: getOrgIdForFetchExternalWorklist() ?? "",
        mrn:
          generalOrderDetails?.order?.demographics?.patient_id ??
          generalOrderDetails?.order?.demographics?.patient_mrn ??
          "",
      });
      return CandidService.postAuthorizations(payload);
    },
    [generalOrderDetails],
  );

  /**
   * Refetches order data, closes the dialog, and toasts after a successful
   * authorizations POST.
   *
   * @param result - Authorizations POST envelope
   */
  const onUploadSuccess = useCallback(
    async (result: CandidAuthorizationsResponse) => {
      await fetchAll(
        generalOrderDetails?.order?.order_id ?? "",
        getOrgIdForFetchExternalWorklist() ?? "",
        { force: true },
      );
      dispatch(closeModal());
      toast.success(
        authorizationWritebackToastMessage(result.written_authorizations),
      );
    },
    [fetchAll, generalOrderDetails, dispatch],
  );

  return { onUpload, onUploadSuccess };
};
